import { Injectable } from '@angular/core';
import { Observable, from } from 'rxjs';
import html2canvas from 'html2canvas';
import { ScreenDimensions } from './models/screen.dimensions.model';

export enum ImageTypeEnum {
  JPEG = 'image/jpeg',
  PNG = 'image/png'
}

@Injectable({
  providedIn: 'root'
})
export class ImageService {

  constructor() { }

  canvasToBlob(canvas: HTMLCanvasElement, contentType: ImageTypeEnum, quality: number): Observable<Blob> {
    return new Observable((observer) => {
      if (canvas.toBlob) { // native toBlob support
        canvas.toBlob((blob) => {
          observer.next(blob);
          observer.complete();
        }, contentType, quality);
      } else { // fallback support
        const imageData = canvas.toDataURL(contentType, quality);
        const blob = this.base64ToBlob(imageData, contentType);
        observer.next(blob);
        observer.complete();
      }
    });
  }


  fitCanvas(canvas: HTMLCanvasElement, { width, height }: ScreenDimensions): Observable<HTMLCanvasElement> {
    const thumbnailCanvas = document.createElement('canvas');
    thumbnailCanvas.width = width;
    thumbnailCanvas.height = height;
    
    const thumbnailContext = thumbnailCanvas.getContext('2d');

    // calculate source width and height dimensions
    const thumbanilAspect = thumbnailCanvas.width / thumbnailCanvas.height;
    const sourceHeight = canvas.height;
    const sourceWidth = thumbanilAspect * sourceHeight;

    // calculate source offset
    const offsetX = 0.5 * (canvas.width - sourceWidth);

    // render 3D frame and transfer to thumbnail canvas
    return new Observable((observer) => {
      thumbnailContext.drawImage(canvas, offsetX, 0, sourceWidth, sourceHeight, 0, 0, thumbnailCanvas.width, thumbnailCanvas.height);
      observer.next(thumbnailCanvas);
      observer.complete();
    });
  }

  elementToCanvas(element: HTMLElement, transparent = false): Observable<HTMLCanvasElement> {
    const options = transparent ? { backgroundColor: 'transparent' } : {};
    return from(html2canvas(element, options));
  }

  combineCanvasElements(canvasElements: HTMLCanvasElement[]): HTMLCanvasElement {
    // calculate max bounds
    const width = Math.max(0, ...canvasElements.map((canvas) => canvas.width));
    const height = Math.max(0, ...canvasElements.map((canvas) => canvas.height));

    const targetCanvas = document.createElement('canvas');
    targetCanvas.width = width;
    targetCanvas.height = height;

    // get 2D context for painting each image
    const targetContext = targetCanvas.getContext('2d');

    // paint each canvas to top left, ordered
    canvasElements.forEach((canvas) => {
      targetContext.drawImage(canvas, 0, 0);
    });

    return targetCanvas;
  }

  base64ToBlob(imageData: string, contentType: ImageTypeEnum): Blob {
    const byteString = atob(imageData.split(',')[1]);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: contentType });
  }

  blobToBase64(blob: Blob): Observable<string> {
    return new Observable((observer) => {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        observer.next(<string>reader.result);
        observer.complete();
      });
      reader.readAsDataURL(blob);
    });
  }

  blobToCanvas(blob: Blob, { width, height }: ScreenDimensions): Observable<HTMLCanvasElement> {
    return new Observable((observer) => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      const img = new Image();
      img.addEventListener('load', () => {
        context.drawImage(img, 0, 0);
        observer.next(canvas);
        observer.complete();
      });
      img.src = URL.createObjectURL(blob);
    });
  }
}
